// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Policyholder } from '@polarissleep/shared';
import type { JSX, ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { CardPhoto } from './card-photo';
import type { OTHER_STATE, ServedStateCode } from './eligibility';
import type { PlanChoice } from './plans';
import type { AboutYouAnswers, WhereYouLiveAnswers } from './your-details';

export type { Policyholder };

export type PaymentChoice = 'insurance' | 'self-pay';

export interface CoverageAnswers {
  plan?: PlanChoice;
  /** PlanPayer key, set when the plan has more than one payer (Blue Cross Blue Shield). */
  planPayer?: string;
  /** The payer Organization the plan resolved to; cleared whenever the plan changes. */
  payer?: { key: string; id: string; name: string };
  cardFront?: CardPhoto;
  cardBack?: CardPhoto;
  memberId?: string;
  groupNumber?: string;
  policyholder?: Policyholder;
  /** Set when the policyholder is not the patient. */
  policyholderName?: string;
  /** MM/DD/YYYY, as typed. Set when the policyholder is not the patient. */
  policyholderDateOfBirth?: string;
}

export interface EligibilityAnswers {
  state?: ServedStateCode | typeof OTHER_STATE;
  /** Full state name, set when `state` is "other". */
  otherState?: string;
  /** MM/DD/YYYY, as typed. */
  dateOfBirth?: string;
  /** Ages MINIMUM_AGE–17. Later steps collect guardian details and consent when true. */
  minor?: boolean;
  payment?: PaymentChoice;
}

/**
 * Everything the patient has answered in the v3 booking flow, one section per step.
 * Each step reads and writes only its own section, and submitting sends all sections
 * together, so a new step adds a section here rather than keeping its own state.
 */
export interface IntakeV3Answers {
  eligibility: EligibilityAnswers;
  /** Primary plan, when `eligibility.payment` is insurance. */
  coverage: CoverageAnswers;
  aboutYou: AboutYouAnswers;
  whereYouLive: WhereYouLiveAnswers;
}

export const EMPTY_INTAKE_V3_ANSWERS: IntakeV3Answers = {
  eligibility: {},
  coverage: {},
  aboutYou: {},
  whereYouLive: {},
};

interface IntakeV3AnswersContextValue {
  answers: IntakeV3Answers;
  updateAnswers: <K extends keyof IntakeV3Answers>(section: K, patch: Partial<IntakeV3Answers[K]>) => void;
  resetAnswers: () => void;
}

const IntakeV3AnswersContext = createContext<IntakeV3AnswersContextValue | undefined>(undefined);

/** Holds v3 answers across steps; mounted above the `/intake` routes so moving between steps keeps them. */
export function IntakeV3AnswersProvider({
  children,
  initialAnswers = EMPTY_INTAKE_V3_ANSWERS,
}: {
  children: ReactNode;
  initialAnswers?: IntakeV3Answers;
}): JSX.Element {
  const [answers, setAnswers] = useState<IntakeV3Answers>(initialAnswers);

  const updateAnswers = useCallback(
    <K extends keyof IntakeV3Answers>(section: K, patch: Partial<IntakeV3Answers[K]>): void => {
      setAnswers((current) => ({ ...current, [section]: { ...current[section], ...patch } }));
    },
    []
  );
  const resetAnswers = useCallback((): void => setAnswers(EMPTY_INTAKE_V3_ANSWERS), []);

  const value = useMemo(() => ({ answers, updateAnswers, resetAnswers }), [answers, updateAnswers, resetAnswers]);
  return <IntakeV3AnswersContext.Provider value={value}>{children}</IntakeV3AnswersContext.Provider>;
}

export function useIntakeV3Answers(): IntakeV3AnswersContextValue {
  const context = useContext(IntakeV3AnswersContext);
  if (!context) {
    throw new Error('useIntakeV3Answers must be used inside IntakeV3AnswersProvider');
  }
  return context;
}
